import {
    add as addFNS,
    addDays,
    addMonths,
    addWeeks,
    addYears,
    differenceInCalendarDays,
    differenceInDays,
    differenceInHours,
    differenceInMonths,
    eachDayOfInterval,
    endOfDay,
    endOfISOWeek as endOfISOWeekFNS,
    endOfMonth,
    endOfQuarter,
    endOfYear,
    format,
    getDay,
    getDaysInMonth as getDaysInMonthFNS,
    getISODay,
    getISOWeek,
    isAfter,
    isBefore,
    isEqual,
    isSameMonth,
    isToday as isTodayFNS,
    isValid,
    isWithinInterval,
    parse as parseFNS,
    parseISO,
    roundToNearestHours,
    startOfISOWeek as startOfISOWeekFNS,
    startOfMonth,
    startOfQuarter,
    startOfYear,
} from 'date-fns';
import {
    getVendorTimezone,
    parseDateString,
    formatDateComponents,
    getDatePartsInTimezone,
    getTimeInTimezone,
    getWallClockInTimezone,
    wallClockAsUtcMs,
} from '@/utils/timezone.js';
import { currentLocale } from '@/composables/useLocale.js';

// The names the server speaks, in ISO order: 1 = Monday through 7 = Sunday.
const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/**
 * Composable for handling dates in Vue components
 *
 * @param {Object} options - Optional configuration
 * @param {string} options.timezone - Override timezone (defaults to vendor timezone)
 * @returns {Object} - Date utility functions
 */
export const useDate = (options = {}) => {
    const locale = currentLocale();
    const timezone = options.timezone ?? getVendorTimezone();

    const parse = (dateString) => {
        if (!dateString) return null;

        if (dateString instanceof Date) {
            return isValid(dateString) ? dateString : null;
        }

        const parsed = parseISO(dateString);

        if (!isValid(parsed)) {
            return null;
        }

        // A date-only string names a calendar day, not an instant. Anchored at local
        // noon, it survives being read back in the vendor timezone; midnight
        // lands on the day before for any vendor west of the browser.
        return isDateOnlyString(dateString) ? parseDateOnly(dateString) : parsed;
    };

    const dateIsValid = (date) => {
        if (date === null || date === undefined || date === '') {
            return false;
        }

        if (typeof date === 'string') {
            const trimmed = date.trim();
            if (trimmed === '') return false;

            const parsed = parseISO(trimmed);
            return isValid(parsed);
        }

        if (date instanceof Date) {
            return isValid(date) && !isNaN(date.getTime());
        }

        if (typeof date === 'number') {
            const dateFromNumber = new Date(date);
            return isValid(dateFromNumber) && !isNaN(dateFromNumber.getTime());
        }

        return false;
    };

    // Returns the number of hours in a date (in vendor timezone)
    const getHours = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;

        return getWallClockInTimezone(new Date(dateObj), timezone).hour;
    };

    // Returns the time in a date in the format HH:MM
    // For UTC strings (ending in Z), uses timezone-aware conversion
    // For other strings (local times or explicit offsets), extracts time directly
    const getTime = (date) => {
        if (!date) return null;

        // If it's a string, check if it's UTC (ends with Z) and needs timezone conversion
        if (typeof date === 'string') {
            const isUtcTime = date.endsWith('Z');

            // For non-UTC strings, extract time directly (preserves local time from string)
            if (!isUtcTime) {
                // Match time in "yyyy-MM-dd HH:mm:ss" or "yyyy-MM-dd HH:mm" format
                const simpleMatch = date.match(/\s(\d{2}):(\d{2})(?::\d{2})?$/);
                if (simpleMatch) {
                    return `${simpleMatch[1]}:${simpleMatch[2]}`;
                }

                // Match time in ISO8601 format "yyyy-MM-ddTHH:mm:ss" (with or without offset)
                const isoMatch = date.match(/T(\d{2}):(\d{2})/);
                if (isoMatch) {
                    return `${isoMatch[1]}:${isoMatch[2]}`;
                }
            }
            // For UTC strings (Z suffix), fall through to timezone-aware formatting
        }

        // Use timezone-aware formatting for UTC times and Date objects
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;

        return getTimeInTimezone(new Date(dateObj), timezone);
    };

    const getDaysInMonth = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        return getDaysInMonthFNS(dateObj);
    };

    const getWeekNumber = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        return getISOWeek(dateObj); // Start week on Monday
    };

    // Differences
    const hoursDiff = (date1, date2) => {
        const d1 = typeof date1 === 'string' ? parse(date1) : date1;
        const d2 = typeof date2 === 'string' ? parse(date2) : date2;

        return d1 && d2 ? differenceInHours(d1, d2) : 0;
    };

    // Pure hours difference calculated later hour (rounded ceiling) and earlier hour (rounded floor)
    // This is useful for calculating the difference in hours without considering minutes
    const pureHoursDiff = (date1, date2) => {
        const d1 = typeof date1 === 'string' ? parse(date1) : date1;
        const d2 = typeof date2 === 'string' ? parse(date2) : date2;
        if (!d1 || !d2) return 0;

        const hours1 = roundToNearestHours(d1, { roundingMethod: 'ceil' });
        const hours2 = roundToNearestHours(d2, { roundingMethod: 'floor' });
        return differenceInHours(hours1, hours2);
    };

    const daysDiff = (date1, date2) => {
        const d1 = typeof date1 === 'string' ? parse(date1) : date1;
        const d2 = typeof date2 === 'string' ? parse(date2) : date2;

        return d1 && d2 ? differenceInDays(d1, d2) : 0;
    };

    const calendarDaysDiff = (date1, date2) => {
        const d1 = typeof date1 === 'string' ? parse(date1) : date1;
        const d2 = typeof date2 === 'string' ? parse(date2) : date2;
        if (!d1 || !d2) return 0;

        // Get the date components in the specified timezone to correctly count calendar days
        // This ensures that a time like 21:00 in America/New_York (which is 02:00 UTC next day)
        // is counted as the same calendar day as 08:00 in America/New_York
        const getVendorDateParts = (date) => {
            const { y, m, d } = getDatePartsInTimezone(new Date(date), timezone);
            // Create a date at noon to avoid any edge cases
            return new Date(y, m, d, 12, 0, 0);
        };

        const vendorDate1 = getVendorDateParts(d1);
        const vendorDate2 = getVendorDateParts(d2);

        return differenceInCalendarDays(vendorDate1, vendorDate2);
    };

    const monthsDiff = (date1, date2) => {
        const d1 = typeof date1 === 'string' ? parse(date1) : date1;
        const d2 = typeof date2 === 'string' ? parse(date2) : date2;

        return d1 && d2 ? differenceInMonths(d1, d2) : 0;
    };

    // Boundaries
    const startOf = (date, unit = 'day') => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj || !isValid(dateObj)) return null;

        if (unit === 'day') {
            // Get the date components (Y-M-D) in the specified timezone
            const { y, m, d } = getDatePartsInTimezone(new Date(dateObj), timezone);

            // Create a string for midnight in the target timezone
            const timeString = `${formatDateComponents(y, m + 1, d)} 00:00:00`;

            // Parse as if it's UTC
            const tempDate = new Date(`${timeString.replace(' ', 'T')}Z`);

            const offsetMs = wallClockAsUtcMs(tempDate, timezone) - tempDate.getTime();

            // Return the UTC Date that represents midnight in the target timezone
            return new Date(tempDate.getTime() - offsetMs);
        }

        switch (unit) {
            case 'isoWeek':
                return startOfISOWeekFNS(dateObj);
            case 'month':
                return startOfMonth(dateObj);
            case 'quarter':
                return startOfQuarter(dateObj);
            case 'year':
                return startOfYear(dateObj);
            default:
                return dateObj;
        }
    };

    const endOf = (date, unit = 'day') => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj || !isValid(dateObj)) return null;

        switch (unit) {
            case 'day':
                return endOfDay(dateObj);
            case 'isoWeek':
                return endOfISOWeekFNS(dateObj);
            case 'month':
                return endOfMonth(dateObj);
            case 'quarter':
                return endOfQuarter(dateObj);
            case 'year':
                return endOfYear(dateObj);
            default:
                return dateObj;
        }
    };

    const daysBetweenDateTimes = (startDateTime, endDateTime) => {
        const start = startOf(startDateTime, 'day');
        const end = startOf(endDateTime, 'day');

        return daysDiff(end, start);
    };

    // Format with localization (all formatting uses vendor timezone)

    // Format a local date string to a human-readable format
    // Example (en): '2023-10-01T14:13' ->  'October 1, 2023'
    // Example (da): '2023-10-01T14:13' ->  '1. oktober 2023'
    const longDate = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        return new Intl.DateTimeFormat(locale, {
            timeZone: timezone,
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        }).format(new Date(dateObj));
    };

    // The week as single localized letters, opening on the day the locale opens on -
    // Monday across most of Europe, Sunday in the US - paired with the English weekday
    // name the server speaks, for a compact strip of markers.
    // Example (da): [{ day: 'monday', initial: 'M' }, { day: 'tuesday', initial: 'T' }, ...]
    const weekdayInitials = () => {
        const asLocale = new Intl.Locale(locale);
        const weekInfo = typeof asLocale.getWeekInfo === 'function' ? asLocale.getWeekInfo() : asLocale.weekInfo;

        // Monday where the runtime cannot say.
        const firstDay = weekInfo?.firstDay ?? 1;
        const initial = new Intl.DateTimeFormat(locale, { timeZone: 'UTC', weekday: 'narrow' });

        return Array.from({ length: 7 }, (_, index) => {
            const offset = (firstDay - 1 + index) % 7;

            return {
                day: WEEKDAYS[offset],
                // 2024-01-08 was a Monday, so the offset walks the week from there.
                initial: initial.format(new Date(Date.UTC(2024, 0, 8 + offset))),
            };
        });
    };

    // Format month and day without year
    // Example (en): '2023-10-01T14:13' -> 'October 1'
    // Example (da): '2023-10-01T14:13' -> '1. oktober'
    const monthDay = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        return new Intl.DateTimeFormat(locale, {
            timeZone: timezone,
            month: 'long',
            day: 'numeric',
        }).format(new Date(dateObj));
    };

    // Format a long date with time format
    // example (en): '2023-10-01T14:13' -> 'October 1, 2023, 14:13'
    // example (da): '2023-10-01T14:13' -> '1. oktober 2023, 14.13'
    const longDateWithTime = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        const dateOptions = { timeZone: timezone, year: 'numeric', month: 'long', day: 'numeric' };
        const timeOptions = { timeZone: timezone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };

        let localDate = new Intl.DateTimeFormat(locale, dateOptions).format(new Date(dateObj));
        let localTime = new Intl.DateTimeFormat(locale, timeOptions).format(new Date(dateObj));
        return localDate + ', ' + localTime;
    };

    // Weekday with shortdate format
    // example (en): '2023-10-01T14:13' -> 'Sunday, 01/10/23'
    // example (da): '2023-10-01T14:13' -> 'Søndag, 01/10/23'
    const weekdayShortDate = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        const weekDay = new Intl.DateTimeFormat(locale, { timeZone: timezone, weekday: 'long' }).format(
            new Date(dateObj)
        );
        const shortDate = new Intl.DateTimeFormat(locale, {
            timeZone: timezone,
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
        }).format(new Date(dateObj));
        return `${weekDay}, ${shortDate}`;
    };

    // Format month and year
    // example (en): '2023-10-01T14:13' -> 'October 2023'
    // example (da): '2023-10-01T14:13' -> 'oktober 2023'
    const monthYear = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        return new Intl.DateTimeFormat(locale, {
            timeZone: timezone,
            month: 'long',
            year: 'numeric',
        }).format(new Date(dateObj));
    };

    // Format weekday and day
    // example (en): '2023-10-01T14:13' -> 'Sun 01'
    // example (da): '2023-10-01T14:13' -> 'søn 01'
    const weekdayDay = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        const weekday = new Intl.DateTimeFormat(locale, {
            timeZone: timezone,
            weekday: 'short',
        })
            .format(new Date(dateObj))
            .slice(0, 3); // Get the first 3 letters of the weekday
        const day = new Intl.DateTimeFormat(locale, {
            timeZone: timezone,
            day: '2-digit',
        }).format(new Date(dateObj));
        return `${weekday} ${day}`;
    };

    // Short weekday, day number and month as separate parts
    // example (en): '2023-10-01T14:13' -> { weekday: 'Sun', day: '1', month: 'Oct' }
    // example (da): '2023-10-01T14:13' -> { weekday: 'søn.', day: '1', month: 'okt.' }
    const shortDateParts = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) {
            return null;
        }

        const timeZone = typeof date === 'string' && isDateOnlyString(date) ? undefined : timezone;

        const formatPart = (options) => {
            return new Intl.DateTimeFormat(locale, { timeZone, ...options }).format(new Date(dateObj));
        };

        return {
            weekday: formatPart({ weekday: 'short' }),
            day: formatPart({ day: 'numeric' }),
            month: formatPart({ month: 'short' }),
        };
    };

    // Format relative time to now
    // This function formats a date string to a relative time string (e.g., "2 days ago")
    // Example (da): for 7 sekunder siden
    // Example (en): 7 seconds ago
    const formatRelativeTime = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        const parsedDate = new Date(dateObj);
        const now = new Date();
        const diffInSeconds = Math.floor((now - parsedDate) / 1000);
        const absDiff = Math.abs(diffInSeconds);
        const isPast = diffInSeconds > 0;

        const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
        const sign = isPast ? -1 : 1;

        if (absDiff < 60) {
            return rtf.format(sign * Math.floor(absDiff), 'second');
        }
        if (absDiff < 3600) {
            return rtf.format(sign * Math.floor(absDiff / 60), 'minute');
        }
        if (absDiff < 86400) {
            return rtf.format(sign * Math.floor(absDiff / 3600), 'hour');
        }
        if (absDiff < 2592000) {
            return rtf.format(sign * Math.floor(absDiff / 86400), 'day');
        }
        if (absDiff < 31536000) {
            return rtf.format(sign * Math.floor(absDiff / 2592000), 'month');
        }
        return rtf.format(sign * Math.floor(absDiff / 31536000), 'year');
    };

    const parseDate = (date, formatString, forceTimezone = false) => {
        const formattedDate = typeof date === 'string' ? parse(date) : date;
        if (!isValid(formattedDate)) return null;

        // Check if format includes time components (H, h, m, s)
        const hasTimeComponent = /[Hhms]/.test(formatString);

        if (hasTimeComponent || forceTimezone) {
            const { year, month, day, hour, minute, second } = getWallClockInTimezone(
                new Date(formattedDate),
                timezone
            );

            const adjustedDate = new Date(year, month - 1, day, hour, minute, second);

            return format(adjustedDate, formatString);
        }

        // For date-only formats, use date-fns directly
        return format(formattedDate, formatString);
    };

    // Manipulation

    // Add or subtract days, months, or years from a date (hours, minutes, seconds are not supported)
    const add = (date, amount, unit = 'days') => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;

        switch (unit) {
            case 'days':
                return addDays(dateObj, amount);
            case 'weeks':
                return addWeeks(dateObj, amount);
            case 'months':
                return addMonths(dateObj, amount);
            case 'years':
                return addYears(dateObj, amount);
            default:
                return dateObj;
        }
    };

    // Subtract days, months, or years from a date (hours, minutes, seconds are not supported)
    const subtract = (date, amount, unit = 'days') => {
        return add(date, -amount, unit);
    };

    // Add or subtract any part of the date by passing an object with the parts to add or subtract
    // Example: addOrSubtract(new Date(), { days: 5, months: 2, years: 1 });
    // For subtraction, use negative values: addOrSubtract(new Date(), { days: -5, months: -2, years: -1 });
    const addOrSubtract = (date, addObj) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;

        return addFNS(dateObj, addObj);
    };

    // Comparisons
    const isAfterDate = (date1, date2) => {
        const d1 = typeof date1 === 'string' ? parse(date1) : date1;
        const d2 = typeof date2 === 'string' ? parse(date2) : date2;
        return d1 && d2 ? isAfter(d1, d2) : false;
    };

    const isBeforeDate = (date1, date2) => {
        const d1 = typeof date1 === 'string' ? parse(date1) : date1;
        const d2 = typeof date2 === 'string' ? parse(date2) : date2;
        return d1 && d2 ? isBefore(d1, d2) : false;
    };

    const isEqualDate = (date1, date2) => {
        const d1 = typeof date1 === 'string' ? parse(date1) : date1;
        const d2 = typeof date2 === 'string' ? parse(date2) : date2;
        return d1 && d2 ? isEqual(d1, d2) : false;
    };

    const isSameDay = (date1, date2) => {
        const d1 = typeof date1 === 'string' ? parse(date1) : date1;
        const d2 = typeof date2 === 'string' ? parse(date2) : date2;
        if (!d1 || !d2) return false;

        // Compare dates in the configured timezone to handle cross-timezone scenarios
        const dayIn = (date) => getDatePartsInTimezone(new Date(date), timezone).dateYmd;

        return dayIn(d1) === dayIn(d2);
    };

    const daysInMonth = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        return getDaysInMonth(dateObj);
    };

    const isoWeekday = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        return getISODay(dateObj); // Returns 1-7 (Monday-Sunday)
    };

    /**
     * Today in the vendor's timezone, as a calendar day at local noon like parseDateOnly
     * @returns {Date}
     */
    const today = () => {
        const { y, m, d } = getDatePartsInTimezone(new Date(), timezone);
        return new Date(y, m, d, 12, 0, 0);
    };

    const isToday = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return false;
        return isTodayFNS(dateObj);
    };

    const eachDayOfIntervalDates = (start, end) => {
        const startDate = typeof start === 'string' ? parse(start) : start;
        const endDate = typeof end === 'string' ? parse(end) : end;
        if (!startDate || !endDate) return [];
        return eachDayOfInterval({ start: startDate, end: endDate });
    };

    const getDayOfWeek = (date) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj) return null;
        return getDay(dateObj);
    };

    const isSameMonthDates = (date1, date2) => {
        const d1 = typeof date1 === 'string' ? parse(date1) : date1;
        const d2 = typeof date2 === 'string' ? parse(date2) : date2;
        return d1 && d2 ? isSameMonth(d1, d2) : false;
    };

    const isWithinIntervalDates = (date, start, end) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        const startDate = typeof start === 'string' ? parse(start) : start;
        const endDate = typeof end === 'string' ? parse(end) : end;
        if (!dateObj || !startDate || !endDate) return false;
        return isWithinInterval(dateObj, { start: startDate, end: endDate });
    };

    const parseString = (dateString, formatString, referenceDate) => {
        return parseFNS(dateString, formatString, referenceDate || new Date());
    };

    // Format a date range as a human-readable string
    // Same date: "14 March 2026"
    // Same month: "12 - 15 March 2026"
    // Different months, same year: "28 February - 3 March 2026"
    // Different years: "30 December 2025 - 2 January 2026"
    // Pass { month: 'short' } where the space is tight: "12 - 15 Mar 2026"
    const dateRange = (startDate, endDate, { month = 'long' } = {}) => {
        const start = typeof startDate === 'string' ? parse(startDate) : startDate;
        const end = typeof endDate === 'string' ? parse(endDate) : endDate;
        if (!start || !end) return null;

        const formatParts = (date, options) =>
            new Intl.DateTimeFormat(locale, { timeZone: timezone, ...options }).format(new Date(date));

        const startDay = formatParts(start, { day: 'numeric' });
        const endDay = formatParts(end, { day: 'numeric' });
        const startMonth = formatParts(start, { month });
        const endMonth = formatParts(end, { month });
        const startYear = formatParts(start, { year: 'numeric' });
        const endYear = formatParts(end, { year: 'numeric' });

        if (isSameDay(start, end)) {
            return `${startDay} ${startMonth} ${startYear}`;
        }

        if (startYear !== endYear) {
            return `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`;
        }

        if (startMonth !== endMonth) {
            return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${endYear}`;
        }

        return `${startDay} - ${endDay} ${startMonth} ${startYear}`;
    };

    const formatDate = (date, formatString) => {
        const dateObj = typeof date === 'string' ? parse(date) : date;
        if (!dateObj || !isValid(dateObj)) return null;
        return format(dateObj, formatString);
    };

    /**
     * Check if a string is a date-only format (YYYY-MM-DD)
     * @param {string} str - String to check
     * @returns {boolean}
     */
    const isDateOnlyString = (str) => {
        return typeof str === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(str);
    };

    /**
     * Parse a date-only string (YYYY-MM-DD) as a local Date at noon
     * Avoids timezone shift that occurs with new Date("YYYY-MM-DD")
     * @param {string} dateString - Date string in YYYY-MM-DD format
     * @returns {Date|null}
     */
    const parseDateOnly = (dateString) => {
        const parts = parseDateString(dateString);
        if (!parts) return null;
        // Use noon to avoid timezone boundary issues
        return new Date(parts.year, parts.month - 1, parts.day, 12, 0, 0);
    };

    /**
     * Format a date-only string to display format without timezone conversion
     * @param {string} dateString - Date string in YYYY-MM-DD format
     * @param {string} displayFormat - Output format: 'dd/MM/yy', 'dd/MM/yyyy', etc.
     * @returns {string|null}
     */
    const formatDateOnly = (dateString, displayFormat = 'dd/MM/yy') => {
        const parts = parseDateString(dateString);
        if (!parts) return null;
        return formatDateComponents(parts.year, parts.month, parts.day, displayFormat);
    };

    /**
     * Encode a Date as a comparable integer (yyyymmdd) in the browser-local calendar
     * day, independent of the vendor timezone. Expects a valid Date.
     * @param {Date} date
     * @returns {number}
     */
    const toCalendarDayNumber = (date) => date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();

    /**
     * Compare two Dates by browser-local calendar day, independent of the vendor
     * timezone (unlike isSameDay). Returns false for non-Date or invalid inputs.
     * @param {Date} date1
     * @param {Date} date2
     * @returns {boolean}
     */
    const isSameCalendarDay = (date1, date2) => {
        if (!(date1 instanceof Date) || !(date2 instanceof Date)) return false;
        if (!dateIsValid(date1) || !dateIsValid(date2)) return false;

        return toCalendarDayNumber(date1) === toCalendarDayNumber(date2);
    };

    /**
     * Resolve a date-picker bound to a Date whose LOCAL y/m/d is the intended calendar
     * day. Date-only strings go through parseDateOnly (local noon) to avoid the
     * new Date('yyyy-MM-dd') UTC-midnight shift; anything else through parse. Never
     * applies vendor-tz startOf(), which would shift the boundary east of the vendor.
     * @param {Date|string|null|undefined} value
     * @returns {Date|null}
     */
    const toCalendarDate = (value) => {
        if (!value) return null;
        if (isDateOnlyString(value)) return parseDateOnly(value);

        const parsed = parse(value);
        return parsed && dateIsValid(parsed) ? parsed : null;
    };

    /**
     * Check if a string is a vendor-local datetime format (YYYY-MM-DD HH:mm:ss)
     * These are datetimes already converted to vendor timezone
     * @param {string} str - String to check
     * @returns {boolean}
     */
    const isVendorLocalDateTimeString = (str) => {
        return typeof str === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/.test(str);
    };

    /**
     * Format a vendor-local datetime string to display format without timezone conversion
     * @param {string} dateTimeString - DateTime string in "YYYY-MM-DD HH:mm:ss" format
     * @param {string} displayFormat - Output format (currently only 'dd/MM/yy, HH:mm' supported)
     * @returns {string|null}
     */
    const formatVendorLocalDateTime = (dateTimeString, displayFormat = 'dd/MM/yy, HH:mm') => {
        if (!isVendorLocalDateTimeString(dateTimeString)) return null;
        const [datePart, timePart] = dateTimeString.split(' ');
        const [year, month, day] = datePart.split('-');
        const [hours, minutes] = timePart.split(':');
        if (displayFormat === 'dd/MM/yy, HH:mm') {
            return `${day}/${month}/${year.slice(-2)}, ${hours}:${minutes}`;
        }
        return dateTimeString;
    };

    return {
        startOf,
        endOf,
        parse,
        daysDiff,
        monthsDiff,
        daysBetweenDateTimes,
        longDateWithTime,
        parseDate,
        isAfterDate,
        isBeforeDate,
        isEqualDate,
        isSameDay,
        add,
        addOrSubtract,
        subtract,
        daysInMonth,
        isoWeekday,
        getHours,
        calendarDaysDiff,
        hoursDiff,
        getDaysInMonth,
        today,
        isToday,
        weekdayShortDate,
        longDate,
        weekdayInitials,
        monthDay,
        formatRelativeTime,
        monthYear,
        weekdayDay,
        shortDateParts,
        getWeekNumber,
        pureHoursDiff,
        getTime,
        dateIsValid,
        eachDayOfIntervalDates,
        getDayOfWeek,
        isSameMonthDates,
        isWithinIntervalDates,
        parseString,
        formatDate,
        dateRange,
        isDateOnlyString,
        parseDateOnly,
        formatDateOnly,
        toCalendarDayNumber,
        isSameCalendarDay,
        toCalendarDate,
        isVendorLocalDateTimeString,
        formatVendorLocalDateTime,
    };
};
